// src/pages/TodoList/TodoList.jsx

import React, { useState, useEffect } from 'react';
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

// --- STYLES ---
const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent',
        padding: '24px'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '28px',
        fontWeight: '600',
        color: '#1a1d29',
        margin: 0
    },
    addForm: {
        background: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '24px'
    },
    inputGroup: {
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-end'
    },
    input: {
        flex: 1,
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none'
    },
    textarea: {
        flex: 1,
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        resize: 'vertical',
        minHeight: '40px'
    },
    prioritySelect: {
        padding: '12px 16px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '14px',
        outline: 'none',
        minWidth: '120px'
    },
    addButton: {
        padding: '12px 24px',
        backgroundColor: '#06C167',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    todoList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    todoItem: {
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    completedItem: {
        opacity: 0.7,
        textDecoration: 'line-through'
    },
    checkbox: {
        width: '20px',
        height: '20px',
        cursor: 'pointer'
    },
    todoContent: {
        flex: 1
    },
    todoTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1a1d29',
        marginBottom: '4px'
    },
    todoDescription: {
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '8px'
    },
    todoMeta: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center'
    },
    priorityBadge: {
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '600'
    },
    priorityHigh: {
        backgroundColor: '#fef2f2',
        color: '#dc2626'
    },
    priorityMedium: {
        backgroundColor: '#fef3c7',
        color: '#d97706'
    },
    priorityLow: {
        backgroundColor: '#f0f9ff',
        color: '#2563eb'
    },
    todoDate: {
        fontSize: '12px',
        color: '#9ca3af'
    },
    actionButtons: {
        display: 'flex',
        gap: '8px'
    },
    deleteButton: {
        padding: '8px 12px',
        backgroundColor: '#dc2626',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '12px',
        cursor: 'pointer'
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px',
        color: '#6b7280'
    }
};

function TodoList() {
    const { userData, currentUser } = useAuth();
    const [todos, setTodos] = useState([]);
    const [newTodo, setNewTodo] = useState({
        title: '',
        description: '',
        priority: 'medium'
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const todosQuery = query(
            collection(db, 'companies', userData.companyId, 'todos'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(todosQuery, (snapshot) => {
            const todoList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setTodos(todoList);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userData]);

    const handleAddTodo = async (e) => {
        e.preventDefault();
        if (!newTodo.title.trim() || !userData?.companyId) return;

        try {
            await addDoc(collection(db, 'companies', userData.companyId, 'todos'), {
                title: newTodo.title.trim(),
                description: newTodo.description.trim(),
                priority: newTodo.priority,
                completed: false,
                createdAt: new Date(),
                createdBy: currentUser?.email?.split('@')[0] || 'Bilinmeyen Kullanıcı',
                createdByEmail: currentUser?.email || 'Bilinmeyen'
            });

            setNewTodo({ title: '', description: '', priority: 'medium' });
        } catch (error) {
            console.error('Todo eklenirken hata:', error);
        }
    };

    const handleToggleComplete = async (todoId, completed) => {
        try {
            const todoRef = doc(db, 'companies', userData.companyId, 'todos', todoId);
            await updateDoc(todoRef, { completed: !completed });
        } catch (error) {
            console.error('Todo güncellenirken hata:', error);
        }
    };

    const handleDeleteTodo = async (todoId) => {
        if (window.confirm('Bu görevi silmek istediğinizden emin misiniz?')) {
            try {
                await deleteDoc(doc(db, 'companies', userData.companyId, 'todos', todoId));
            } catch (error) {
                console.error('Todo silinirken hata:', error);
            }
        }
    };

    const getPriorityStyle = (priority) => {
        switch (priority) {
            case 'high': return { ...styles.priorityBadge, ...styles.priorityHigh };
            case 'medium': return { ...styles.priorityBadge, ...styles.priorityMedium };
            case 'low': return { ...styles.priorityBadge, ...styles.priorityLow };
            default: return { ...styles.priorityBadge, ...styles.priorityMedium };
        }
    };

    const getPriorityText = (priority) => {
        switch (priority) {
            case 'high': return 'Yüksek';
            case 'medium': return 'Orta';
            case 'low': return 'Düşük';
            default: return 'Orta';
        }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.emptyState}>Yapılacaklar yükleniyor...</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Yapılacaklar Listesi</h1>
            </div>

            {/* Add Todo Form */}
            <div style={styles.addForm}>
                <form onSubmit={handleAddTodo}>
                    <div style={styles.inputGroup}>
                        <div style={{ flex: 2 }}>
                            <input
                                type="text"
                                placeholder="Görev başlığı..."
                                value={newTodo.title}
                                onChange={(e) => setNewTodo({ ...newTodo, title: e.target.value })}
                                style={styles.input}
                                required
                            />
                        </div>
                        <div style={{ flex: 3 }}>
                            <textarea
                                placeholder="Açıklama (opsiyonel)"
                                value={newTodo.description}
                                onChange={(e) => setNewTodo({ ...newTodo, description: e.target.value })}
                                style={styles.textarea}
                                rows="1"
                            />
                        </div>
                        <select
                            value={newTodo.priority}
                            onChange={(e) => setNewTodo({ ...newTodo, priority: e.target.value })}
                            style={styles.prioritySelect}
                        >
                            <option value="low">Düşük Öncelik</option>
                            <option value="medium">Orta Öncelik</option>
                            <option value="high">Yüksek Öncelik</option>
                        </select>
                        <button type="submit" style={styles.addButton}>
                            Ekle
                        </button>
                    </div>
                </form>
            </div>

            {/* Todo List */}
            <div style={styles.todoList}>
                {todos.length === 0 ? (
                    <div style={styles.emptyState}>
                        <p>Henüz yapılacak görev bulunmuyor.</p>
                        <p>Yukarıdaki formu kullanarak yeni görev ekleyebilirsiniz.</p>
                    </div>
                ) : (
                    todos.map(todo => (
                        <div
                            key={todo.id}
                            style={{
                                ...styles.todoItem,
                                ...(todo.completed ? styles.completedItem : {})
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={todo.completed}
                                onChange={() => handleToggleComplete(todo.id, todo.completed)}
                                style={styles.checkbox}
                            />
                            <div style={styles.todoContent}>
                                <div style={styles.todoTitle}>{todo.title}</div>
                                {todo.description && (
                                    <div style={styles.todoDescription}>{todo.description}</div>
                                )}
                                <div style={styles.todoMeta}>
                                    <span style={getPriorityStyle(todo.priority)}>
                                        {getPriorityText(todo.priority)}
                                    </span>
                                    <span style={styles.todoDate}>
                                        {todo.createdAt?.toDate?.()?.toLocaleDateString('tr-TR') || 'Tarih yok'}
                                    </span>
                                    <span style={styles.todoDate}>
                                        Oluşturan: {todo.createdBy || 'Bilinmeyen'}
                                    </span>
                                </div>
                            </div>
                            <div style={styles.actionButtons}>
                                <button
                                    onClick={() => handleDeleteTodo(todo.id)}
                                    style={styles.deleteButton}
                                >
                                    Sil
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default TodoList;